import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { fetchCsvExport } from '@/lib/utils/exports'
import { stringifyValues } from '@/lib/utils/queries'
import { getApi } from '@/stores/api'
import {
  CHANNEL_VALUES,
  MATERIAL_STATUSES,
  PRODUCT_CATEGORIES,
  RELEASE_STATUSES,
  type ProductCategory,
} from '@repo/constants'
import { monthStartString } from '@repo/zod-schemas'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

// Price List
export const priceListSearchSchema = z.object({
  // The period whose price list copy is listed; mirrored from the header selection.
  crtPeriod: monthStartString.optional(),
  keyword: z.string().optional(),
  province: z.array(z.string()).optional(),
  channel: z.array(z.enum(CHANNEL_VALUES)).optional(),
  status: z.array(z.enum(MATERIAL_STATUSES)).optional(),
  marked: z.boolean().optional(),
  ...datatableSearchSchema.shape,
})

export type PriceListSearch = z.infer<typeof priceListSearchSchema>

export async function getPriceList(search: PriceListSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt.materials['price-list'].$get(
    { query: stringifyValues(search) },
    { init: { signal } },
  )

  const priceList = await res.json()
  return priceList
}

export type PricedMaterial = Awaited<ReturnType<typeof getPriceList>>['results'][number]

export function usePriceList(queries: PriceListSearch) {
  return useQuery({
    queryKey: ['priceList', queries],
    queryFn: ({ signal }) => getPriceList(queries, signal),
  })
}

async function getPriceListCsv(search: PriceListSearch, signal?: AbortSignal) {
  const queryParams = new URLSearchParams(stringifyValues(search)).toString()

  const csvExport = await fetchCsvExport(`/crt/materials/price-list/export?${queryParams}`, signal)
  return csvExport
}

export const priceListExportQueryOptions = (queries: PriceListSearch) => ({
  queryKey: ['priceListExport', queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getPriceListCsv(queries, signal),
})

// Materials List
export const materialsListSearchSchema = z.object({
  keyword: z.string().optional(),
  materialGroupCode: z.array(z.string()).optional(),
  productCategory: z.array(z.enum(PRODUCT_CATEGORIES)).optional(),
  brand: z.array(z.string()).optional(),
  brandCode: z.array(z.string()).optional(),
  variant: z.array(z.string()).optional(),
  packContentSize: z.array(z.string()).optional(),
  priceSegment: z.array(z.string()).optional(),
  productType: z.array(z.string()).optional(),
  marking: z.array(z.string()).optional(),
  status: z.array(z.enum(MATERIAL_STATUSES)).optional(),
  releaseStatus: z.array(z.enum(RELEASE_STATUSES)).optional(),
  isFmcPackSizeStandard: z.boolean().optional(),
  ...datatableSearchSchema.shape,
})

export type MaterialsListSearch = z.infer<typeof materialsListSearchSchema>

export async function getMaterialsList(search: MaterialsListSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt.materials['materials-list'].$get(
    { query: stringifyValues(search) },
    { init: { signal } },
  )

  const materialsList = await res.json()
  return materialsList
}

export type MaterialsListItem = Awaited<ReturnType<typeof getMaterialsList>>['results'][number]

export function useMaterialsList(queries: MaterialsListSearch) {
  return useQuery({
    queryKey: ['materialsList', queries],
    queryFn: ({ signal }) => getMaterialsList(queries, signal),
  })
}

async function getMaterialsListCsv(search: MaterialsListSearch, signal?: AbortSignal) {
  const queryParams = new URLSearchParams(stringifyValues(search)).toString()

  const csvExport = await fetchCsvExport(
    `/crt/materials/materials-list/export?${queryParams}`,
    signal,
  )
  return csvExport
}

export const materialsListExportQueryOptions = (queries: MaterialsListSearch) => ({
  queryKey: ['materialsListExport', queries],
  queryFn: ({ signal }: { signal: AbortSignal }) => getMaterialsListCsv(queries, signal),
})

async function getMaterialProductTypes(productCategory?: ProductCategory, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt.materials['materials-list']['product-types'].$get(
    { query: { productCategory } },
    { init: { signal } },
  )

  const data = await res.json()
  return data.results
}

export function useMaterialProductTypes(productCategory?: ProductCategory) {
  return useQuery({
    queryKey: ['materialProductTypes', productCategory],
    queryFn: ({ signal }) => getMaterialProductTypes(productCategory, signal),
    refetchOnWindowFocus: false,
  })
}

async function getMaterialPackContentSizes(
  productCategory?: ProductCategory,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt.materials['pack-content-sizes'].$get(
    { query: { productCategory } },
    { init: { signal } },
  )

  const packContentSizes = await res.json()
  return packContentSizes
}

export function useMaterialPackContentSizes(productCategory?: ProductCategory) {
  return useQuery({
    queryKey: ['materialPackContentSizes', productCategory],
    queryFn: ({ signal }) => getMaterialPackContentSizes(productCategory, signal),
    refetchOnWindowFocus: false,
  })
}
